/**
 * Qual planilha de rateio monta a Janela de Faturamento de cada mês — inclusive
 * quando a planilha do mês anterior ainda não subiu.
 *
 * Regra de sempre: o rateio do mês M gera as faturas de M+1 (a planilha de
 * AGO/2026 é a base das faturas de SET/2026). Sem a planilha de M, a janela de
 * M+1 não existia: em ABR/2026 ela abriu 28 dias depois da primeira fatura e em
 * AGO/2026, 40 dias depois (788 faturas chegaram antes da planilha). Em
 * OUT/2026 a saída foi copiar à mão o rateio de AGO como se fosse o de SET.
 *
 * Agora, mês sem planilha própria repete a última planilha que existe, inteira
 * — inclusive Cancelada e Pausada: em SET/2026, 45 UCs com cancelamento
 * concluído e 41 pausadas que estavam na planilha ainda geraram boleto com
 * valor, por causa do saldo de créditos (decisão do Felipe, 15/10/2026). Quando
 * a planilha certa sobe, ela assume sozinha; não há cópia para apagar.
 *
 * A mesma regra roda no banco, em `popular_fatura_download_queue` (fila do robô
 * e placeholders "Aguardando Upload"), e no aviso da Vick
 * (`detectarPlanilhaRateioAtrasada`).
 *
 *   node --experimental-strip-types --test src/lib/__tests__/planilhaRateioVigente.test.ts
 */
import { mesDeOrdem, ordemMes } from './conferenciaFaturamento.ts';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

export interface PlanilhaRateio {
  id: string | number;
  mes_referencia?: string | null;
}

export interface MesDaJanela {
  /** Mês das faturas que a planilha alimenta ('OUT/2026'). */
  mesFatura: string;
  /** Mês da planilha usada ('AGO/2026'). */
  planilhaMes: string;
  /** `true` quando a planilha do mês anterior a `mesFatura` não existe e esta foi repetida. */
  repetida: boolean;
}

/** Mês do calendário de uma data, no formato das faturas ('OUT/2026'). */
export const mesDoDia = (d: Date): string => `${MESES[d.getMonth()]}/${d.getFullYear()}`;

/**
 * Para cada planilha, os meses de faturamento que ela alimenta.
 *
 * - Toda planilha alimenta o mês seguinte ao dela.
 * - Mês de faturamento até `mesAtual` cujo mês anterior não tem planilha usa a
 *   mais recente antes dele. Só até o mês corrente: as faturas de um mês chegam
 *   dentro dele (e nas primeiras semanas do seguinte), nunca antes.
 * - Duas planilhas do mesmo mês alimentam os mesmos meses, como já acontecia —
 *   quem junta e tira a UC repetida é a Janela.
 */
export function mesesPorPlanilha<T extends PlanilhaRateio>(
  planilhas: readonly T[],
  mesAtual: string,
): Map<T['id'], MesDaJanela[]> {
  const porOrdem = new Map<number, T[]>();
  for (const p of planilhas) {
    const o = ordemMes(p.mes_referencia);
    if (o === null) continue;
    if (!porOrdem.has(o)) porOrdem.set(o, []);
    porOrdem.get(o)!.push(p);
  }

  const resultado = new Map<T['id'], MesDaJanela[]>();
  if (porOrdem.size === 0) return resultado;

  const anotar = (ordemPlanilha: number, ordemFatura: number, repetida: boolean) => {
    for (const p of porOrdem.get(ordemPlanilha)!) {
      if (!resultado.has(p.id)) resultado.set(p.id, []);
      resultado.get(p.id)!.push({ mesFatura: mesDeOrdem(ordemFatura), planilhaMes: mesDeOrdem(ordemPlanilha), repetida });
    }
  };

  const ordens = [...porOrdem.keys()].sort((a, b) => a - b);
  for (const o of ordens) anotar(o, o + 1, false);

  const atual = ordemMes(mesAtual);
  if (atual === null) return resultado;
  let vigente = ordens[0];
  for (let fatura = ordens[0] + 2; fatura <= atual; fatura++) {
    if (porOrdem.has(fatura - 1)) {
      vigente = fatura - 1;
      continue;
    }
    anotar(vigente, fatura, true);
  }
  return resultado;
}

/** Rótulo do rateio na Janela: "AGO/2026" ou "AGO/2026 (repetido)". */
export const rotuloRateioDaJanela = (nome: string, repetida: boolean): string =>
  repetida ? `${nome} (repetido)` : nome;
